/**
 * API Client for Payment Schedules (Échéanciers de paiement)
 * Handles HTTP requests for managing invoice payment schedules
 */

import api from './api';

export interface PaymentSchedule {
  id: string;
  invoiceId: string;
  dueDate: string;
  status: 'PENDING' | 'PAID' | 'OVERDUE';
  paidAt?: string;
  amount: number;
  installmentNumber: number;
  notes?: string;
  Invoice?: {
    id: string;
    number: string;
    tenantId: string;
    companyId: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

export interface PaymentScheduleStats {
  pending: { count: number; total: number };
  paid: { count: number; total: number };
  overdue: { count: number; total: number };
  // Valeurs pré-calculées par le backend
  totalCount: number;
  paidAmount: number;
  remainingAmount: number;
}

export interface GenerateSchedulesParams {
  numberOfInstallments: number;
  startDate?: string;
  intervalDays?: number;
}

export interface CreateScheduleParams {
  invoiceId: string;
  dueDate: string;
  amount: number;
  installmentNumber: number;
  notes?: string;
}

export interface UpdateScheduleParams {
  dueDate?: string;
  amount?: number;
  installmentNumber?: number;
  notes?: string;
}

export interface PaymentSchedulesResponse {
  success: boolean;
  data: PaymentSchedule[];
  message?: string;
}

export interface PaymentScheduleResponse {
  success: boolean;
  data: PaymentSchedule;
  message?: string;
}

export interface PaymentScheduleStatsResponse {
  success: boolean;
  data: PaymentScheduleStats;
  message?: string;
}

/**
 * API client for Payment Schedules
 */
export const paymentSchedulesAPI = {
  /**
   * Get all payment schedules for an invoice
   */
  async getByInvoice(invoiceId: string): Promise<PaymentSchedule[]> {
    // Note: api.get() uses fetch and returns the JSON directly (not axios)
    const response = await api.get(
      `/billing/invoices/${invoiceId}/payment-schedules`
    ) as PaymentSchedulesResponse;
    // ✅ Defensive: ensure we return an array - response.data is the schedules array
    return response?.data || [];
  },

  /**
   * Get upcoming payment schedules (next N days)
   */
  async getUpcoming(days: number = 30): Promise<PaymentSchedule[]> {
    // Note: api.get() uses fetch and returns the JSON directly (not axios)
    const response = await api.get(
      `/billing/payment-schedules/upcoming?days=${days}`
    ) as PaymentSchedulesResponse;
    // ✅ Defensive: ensure we return an array - response.data is the schedules array
    return response?.data || [];
  },

  /**
   * Get a payment schedule by ID
   */
  async getById(scheduleId: string): Promise<PaymentSchedule> {
    // Note: api.get() uses fetch and returns the JSON directly (not axios)
    const response = await api.get(
      `/billing/payment-schedules/${scheduleId}`
    ) as PaymentScheduleResponse;
    return response.data;
  },

  /**
   * Get statistics for an invoice's payment schedules
   */
  async getStats(invoiceId: string): Promise<PaymentScheduleStats> {
    // Note: api.get() uses fetch and returns the JSON directly (not axios)
    const response = await api.get(
      `/billing/invoices/${invoiceId}/payment-schedules/stats`
    ) as PaymentScheduleStatsResponse;
    return response.data;
  },

  /**
   * Create a new payment schedule
   */
  async create(params: CreateScheduleParams): Promise<PaymentSchedule> {
    // Note: api.post() uses fetch and returns the JSON directly (not axios)
    const response = await api.post(
      '/billing/payment-schedules',
      params
    ) as PaymentScheduleResponse;
    return response.data;
  },

  /**
   * Auto-generate payment schedules for an invoice
   */
  async generate(
    invoiceId: string,
    params: GenerateSchedulesParams
  ): Promise<PaymentSchedule[]> {
    // Note: api.post() uses fetch and returns the JSON directly (not axios)
    // So response IS { success: true, data: [...schedules...] }
    const response = await api.post(
      `/billing/invoices/${invoiceId}/payment-schedules/generate`,
      params
    ) as PaymentSchedulesResponse;
    // ✅ Defensive: ensure we return an array - response.data is the schedules array
    return response?.data || [];
  },

  /**
   * Update a payment schedule
   */
  async update(
    scheduleId: string,
    params: UpdateScheduleParams
  ): Promise<PaymentSchedule> {
    // Note: api.patch() uses fetch and returns the JSON directly (not axios)
    const response = await api.patch(
      `/billing/payment-schedules/${scheduleId}`,
      params
    ) as PaymentScheduleResponse;
    return response.data;
  },

  /**
   * Mark a payment schedule as paid
   */
  async markAsPaid(
    scheduleId: string,
    paidAt?: string
  ): Promise<PaymentSchedule> {
    // Note: api.patch() uses fetch and returns the JSON directly (not axios)
    const response = await api.patch(
      `/billing/payment-schedules/${scheduleId}/mark-paid`,
      { paidAt: paidAt || new Date().toISOString() }
    ) as PaymentScheduleResponse;
    return response.data;
  },

  /**
   * Delete a payment schedule
   */
  async delete(scheduleId: string): Promise<{ success: boolean }> {
    // Note: api.delete() uses fetch and returns the JSON directly (not axios)
    const response = await api.delete(
      `/billing/payment-schedules/${scheduleId}`
    ) as { success: boolean; message?: string };
    return { success: response.success };
  },
};
